import type { SstApiVersion } from "./saLockerDoorInspection"

/**
 * Where every piece of reservation data on the SA locker console came from
 * (LOCKER-355 §8), stated precisely enough to be reproduced.
 *
 * ## Why the page needs to say this at all
 *
 * The reported failure was a door rendering empty while *Fetch from SST v1*
 * returned both sides. Two surfaces disagreed, and nothing on screen said which
 * endpoint either had hit — so the only way to tell a stale snapshot from a
 * mis-pair from a wrong-API read was to open the code. That is the whole reason
 * these strings exist: they are for the moment two panels disagree.
 *
 * ## What "provenance" honestly means per surface, because it differs
 *
 * The Unit section makes exactly ONE live request, and it is to SL2, not to the
 * partner: `GetLockerCurrentReservations`. The partner side is NOT fetched by
 * this page at all — it is the `sstLocker` / `sst2Locker` snapshot stored on the
 * Locker Thing, refreshed only when someone presses Sync. Labelling that
 * "SST v1" without saying it is a stored snapshot would be a worse lie than
 * saying nothing, because it is precisely the staleness case an admin is here
 * to rule out. Each descriptor therefore carries `isLive`.
 *
 * ## No secret ever reaches the screen (LOCKER-334)
 *
 * These URLs are built from ids only. Auth travels in the `Authorization`
 * header that `baseQuerySl2` attaches per request, and none of it is in the URL
 * — but "none of it is in the URL today" is not a guarantee, so `assertNoSecret`
 * runs on every descriptor and a test asserts the invariant rather than the
 * current output.
 */

export type ProvenanceDescriptor = {
  /** Which partner API version this data describes — or `sl2` for our own API. */
  apiVersion: SstApiVersion | "sl2"
  /** i18n key for the one-line human label. */
  labelKey: string
  /**
   * The HTTP method, rendered next to the URL.
   *
   * Not cosmetic, and not defaulted away (review F1.2). The refresh descriptor
   * points at an endpoint that only does the right thing under POST; printing
   * its URL bare made it read as a GET, and a GET to that path lists Things. An
   * admin following the line literally during an incident would get a Thing
   * list back and could reasonably conclude Sync was broken.
   */
  method: "GET" | "POST"
  /** The exact URL, absolute and complete, so it can be pasted into `curl`. */
  url: string
  /**
   * For a POST, the shape of the body the app sends — ids and enum values only,
   * never a payload. Without it "here is a POST URL" is not reproducible.
   */
  bodyHint?: string
  /** False when the value is a stored snapshot rather than a request this view made. */
  isLive: boolean
}

/** Anything that must never appear in a rendered provenance string. */
const SECRET_PATTERN = /(api[-_]?key|authorization|bearer|secret|password|token)/i

/** What a suppressed URL renders as. Never a partial URL — that leaks a prefix. */
export const REDACTED_URL = "[redacted — this URL matched the credential guard]"

/**
 * Suppresses rather than renders. A provenance line is read by a super-admin
 * during an incident and is the single most likely thing on this page to be
 * screenshotted into a ticket — a leaked key would travel further from here
 * than from a log.
 *
 * ## It returns instead of throwing, and that is the corrected behaviour
 *
 * The first version threw (review F1.3). Descriptors are built inside a
 * `useMemo` during render, so a throw does not suppress a line — it unmounts the
 * **whole SA locker console**, the page an admin opened precisely because
 * something was already wrong. And the trigger is not hypothetical-only: the
 * pattern matches anywhere in the URL, and the URL interpolates `tenantId` and
 * `lockerId`, so an id containing `token` would blank the page to prevent a leak
 * the builders make structurally impossible anyway (they interpolate ids; auth
 * travels in `baseQuerySl2`'s header).
 *
 * Failing safe is strictly better here: the guard still refuses to render the
 * value, so no credential reaches the screen, and `console.error` keeps it
 * findable. What it no longer does is take the diagnostic surface down with it.
 */
export const assertNoSecret = (url: string): string => {
  if (SECRET_PATTERN.test(url)) {
    console.error(
      "[saLockerProvenance] refusing to render a URL matching the credential guard",
    )
    return REDACTED_URL
  }
  return url
}

/** Join a configured base with an endpoint path, tolerating either side's slash. */
export const joinUrl = (base: string, path: string): string =>
  `${(base || "").replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`

const withQuery = (url: string, params: Record<string, string | number | undefined>) => {
  const qs = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== "") qs.set(k, String(v))
  })
  const q = qs.toString()
  return q ? `${url}?${q}` : url
}

/**
 * The Unit section's only live read: SL2's own Things API. `ap.limit` is
 * included verbatim because a locker with more reservations than the ceiling
 * would silently under-report, and that was one of the ticket's competing
 * hypotheses — an admin can now see the ceiling instead of guessing at it.
 */
export const reservationsReadProvenance = (args: {
  base: string
  tenantId?: string
  lockerId?: string
  limit: number
}): ProvenanceDescriptor => ({
  apiVersion: "sl2",
  labelKey: "saLockerConsole.provenance.reservations",
  method: "GET",
  url: assertNoSecret(
    withQuery(joinUrl(args.base, "things"), {
      "ap.name": "GetLockerCurrentReservations",
      "ap.tenantId": args.tenantId,
      "ap.lockerId": args.lockerId,
      "ap.limit": args.limit,
      tenantId: args.tenantId,
    }),
  ),
  isLive: true,
})

/**
 * The partner locker snapshot — a STORED value, not a request this view made.
 *
 * The URL given is the REFRESH that produces it, and refresh is a **POST of an
 * ActionReq**, not a read (`SaLockerPage.onRefresh`). The first version of this
 * printed the bare path under a label saying "refresh via", which is wrong twice
 * over: a GET there lists Things, and the doc here claimed pasting it into
 * `curl` would do what Sync does. It would not (review F1.2). The method and the
 * body's shape are part of the descriptor now, because "here is a URL" is not
 * reproducible when the URL only works under one verb.
 *
 * There is deliberately NO url for the read itself: this page never reads the
 * partner. `isLive: false` is the honest answer, and inventing a plausible
 * partner endpoint to fill the field would be exactly the lie the provenance
 * line exists to prevent.
 */
export const snapshotProvenance = (args: {
  base: string
  apiVersion: SstApiVersion
  tenantId?: string
}): ProvenanceDescriptor => ({
  apiVersion: args.apiVersion,
  labelKey: "saLockerConsole.provenance.snapshot",
  method: "POST",
  url: assertNoSecret(
    withQuery(joinUrl(args.base, "things"), { tenantId: args.tenantId }),
  ),
  bodyHint:
    'things[0]={thingTypeMrn:"…:actionReq", actionType:"SstLockerRefresh", executionMode:"sync", tenantId, lockerId, sstLockerId, idempotencyKey}',
  isLive: false,
})

/** The repair panel's live partner read (LOCKER-343, v1 only). */
export const doorFetchProvenance = (args: {
  base: string
  sstLockerId?: string
  sstDoorId?: string
}): ProvenanceDescriptor => ({
  apiVersion: "v1",
  labelKey: "saLockerConsole.provenance.doorFetch",
  method: "GET",
  url: assertNoSecret(
    withQuery(joinUrl(args.base, "proxy/sirsteward-v2"), {
      resourceType: "saLockerDoorFetchV1",
      sstLockerId: args.sstLockerId,
      sstDoorId: args.sstDoorId,
    }),
  ),
  isLive: true,
})

/** `SST V1` / `SST V2` / `SL2` — the chip text, uppercase and unambiguous. */
export const apiVersionLabel = (apiVersion: SstApiVersion | "sl2"): string =>
  apiVersion === "sl2" ? "SL2 API" : `SST ${apiVersion.toUpperCase()}`
